import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { TriviaGroupSummary } from './new-models/TriviaGroupSummary';
import { TriviaGroupGames } from './new-models/TriviaGroupGames';
import { PredictorGroupSummary } from './new-models/PredictorGroupSummary';
import { GroupType } from './new-models/GroupType';
import { ApiService } from '../shared/services/core/api.service';
import { PredictorSelectionViewModel } from './models/predictor-selection-view.model';
import { IPollQuestion } from './new-models/IPollQuestion';
import { PredictorGroupGames } from './new-models/PredictorGroupGames';
import { PollResultModel } from './models/poll-result.model';
import { IGroupSummary } from './new-models/IGroupSummary';
import { ExternalTriviaGroupGames } from './new-models/ExternalTriviaGroupGames';
import { HttpHeaders } from '@angular/common/http';
import { ExternalPollGroupGames } from './new-models/ExternalPollGroupGames';
import { ExternalPredictorGroupGames } from './new-models/ExternalPredictorGroupGames';

@Injectable({ providedIn: 'root' })
export class GamesGroupService {
  constructor(private api: ApiService) {}

  /**
   * Fetches all Trivia game groups
   * @returns Collection of Trivia group summaries
   */
  fetchTriviaGroups(): Observable<TriviaGroupSummary[]> {
    return this.api.get<TriviaGroupSummary>(`games/groups/${GroupType.Trivia}`);
  }

  /**
   * Fetches all Trivia games for a given group
   * @param groupId - The group ID for the trivia game
   * @param unauthorized - Flag indicating if the user is unauthorized - use out of portal games endpoint with GUID header
   * @returns Trivia game group questions, prizes, statuses and sponsor data
   */
  fetchTriviaGroupGames(groupId: number, unauthorized: boolean = false): Observable<TriviaGroupGames> {
    if (unauthorized) {
      return this.api.getAnySingleWithHeaders(`externalgames/trivia/${groupId}/games`, this.getUnauthorizedEntityHeader()).pipe(
        tap((response) => {
          this.setUnauthorizedEntityGuid(response);
        }),
        map((response) => response.body as ExternalTriviaGroupGames)
      );
    }
    return this.api.getAnySingle<TriviaGroupGames>(`games/trivia/${groupId}/games`);
  }

  /**
   * Fetches all Predictor game groups
   * @returns Collection of Predictor group summaries
   */
  fetchPredictorGroups(): Observable<PredictorGroupSummary[]> {
    return this.api.get<PredictorGroupSummary>(`games/groups/${GroupType.Predictor}`);
  }

  /**
   * Fetches a specific games group by its ID
   * @returns Trivia or Predictor group
   */
  fetchGroupById(groupId: number): Observable<IGroupSummary> {
    return this.api.getAnySingle<IGroupSummary>(`games/group/${groupId}`);
  }

  /**
   * Fetches all Predictor games for a given group
   * @param groupId - The group ID for the predictor game
   * @param unauthorized - Flag indicating if the user is unauthorized - use out of portal games endpoint with GUID header
   * @returns Predictor game group questions, prizes, statuses and sponsor data
   */
  fetchPredictorGroupGames(groupId: number, unauthorized: boolean = false): Observable<PredictorGroupGames> {
    if (unauthorized) {
      return this.api
        .getAnySingleWithHeaders(`externalgames/predictor/${groupId}/games`, this.getUnauthorizedEntityHeader())
        .pipe(
          tap((response) => {
            this.setUnauthorizedEntityGuid(response);
          }),
          map((response) => response.body as ExternalPredictorGroupGames)
        );
    }
    return this.api.getAnySingle<PredictorGroupGames>(`games/predictor/${groupId}/games`);
  }

  /**
   * Submits Trivia Answer
   * @returns boolean indicator whether answer was correct
   */
  submitTriviaAnswer(questionId: number, answerId: number, groupId: number, unauthorized: boolean = false) {
    if (unauthorized) {
      return this.api.postWithCustomHeaders(
        `externalgames/trivia/${groupId}/answer`,
        { QuestionId: questionId, AnswerId: answerId },
        this.getUnauthorizedEntityHeader()
      );
    }
    return this.api.post(`games/trivia/${groupId}/answer`, { QuestionId: questionId, AnswerId: answerId });
  }

  /**
   * Submits predictor answers
   * @param groupId - The group ID for the predictor game
   * @param predictorSelection - The user's predictor selections
   * @param unauthorized - Flag indicating if the user is unauthorized - use out of portal games endpoint with GUID header
   */
  submitPredictorAnswers(groupId: number, predictorSelection: PredictorSelectionViewModel, unauthorized: boolean = false) {
    if (unauthorized) {
      return this.api.postWithCustomHeaders(
        `externalgames/predictor/${groupId}`,
        predictorSelection,
        this.getUnauthorizedEntityHeader()
      );
    }
    return this.api.post(`games/predictor/${groupId}`, predictorSelection);
  }

  /**
   * fetch collection of poll questions
   */
  fetchPolls(): Observable<IPollQuestion[]> {
    return this.api.get<IPollQuestion>(`games/polls`);
  }

  /**
   * Submit Poll user answer for specific poll
   * @param pollId unique poll game ID
   * @param answerId selected answerID
   */
  submitPollAnswer(pollId: number, answerId: number, unauthorized: boolean = false): Observable<PollResultModel> {
    if (unauthorized) {
      return this.api.postWithCustomHeaders(
        `externalgames/poll/${pollId}`,
        { AnswerId: answerId },
        this.getUnauthorizedEntityHeader()
      );
    }
    return this.api.post(`games/poll/${pollId}`, { AnswerId: answerId });
  }

  /**
   * Fetches all unauthorized Trivia games for a given group
   * @returns Trivia game group questions, prizes, statuses and sponsor data
   */
  fetchUnauthorizedTrivaGame(groupId: number): Observable<ExternalTriviaGroupGames> {
    const unauthorizedEntityGuid = sessionStorage.getItem('UnauthorizedEntityGuid');
    let headers = new HttpHeaders();
    if (unauthorizedEntityGuid) {
      headers = this.api.getHeaders().set('UnauthorizedEntityGuid', unauthorizedEntityGuid);
    }

    return this.api.getAnySingleWithHeaders(`externalgames/trivia/${groupId}/games`, headers).pipe(
      tap((response) => {
        const unauthorizedEntityGuid = response.headers.get('unauthorizedentityguid');
        if (unauthorizedEntityGuid) {
          sessionStorage.setItem('UnauthorizedEntityGuid', unauthorizedEntityGuid);
        }
      }),
      map((response) => response.body as ExternalTriviaGroupGames)
    );
  }

  /**
   * Submits an answer for an external trivia game
   * @param groupId - The group ID for the trivia game
   * @param answerData - The answer data to submit
   * @returns Observable response from the API
   */
  submitExternalTriviaAnswer(groupId: number, answerData: any): Observable<any> {
    const unauthorizedEntityGuid = sessionStorage.getItem('UnauthorizedEntityGuid');
    if (unauthorizedEntityGuid) {
      const headers = this.api.getHeaders().set('UnauthorizedEntityGuid', unauthorizedEntityGuid);
      return this.api.postWithCustomHeaders(`externalgames/trivia/${groupId}/answer`, answerData, headers);
    } else {
      return this.api.post(`externalgames/trivia/${groupId}/answer`, answerData);
    }
  }

  /**
   * Gets group details by external URL
   * @param externalUrl - The external URL to get group details for
   * @returns Observable with unauthorized game group details
   */
  getGroupDetailsByExternalUrl(externalUrl: string): Observable<any> {
    return this.api.getAnySingle(`externalgames/group/${externalUrl}`);
  }

  /**
   * Fetches all unauthorized Trivia games for a given group
   * @returns Trivia game group questions, prizes, statuses and sponsor data
   */
  fetchUnauthorizedPollGame(groupId: number): Observable<ExternalPollGroupGames> {
    const unauthorizedEntityGuid = sessionStorage.getItem('UnauthorizedEntityGuid');
    let headers = new HttpHeaders();
    if (unauthorizedEntityGuid) {
      headers = this.api.getHeaders().set('UnauthorizedEntityGuid', unauthorizedEntityGuid);
    }

    return this.api.getAnySingleWithHeaders(`externalgames/poll/${groupId}`, headers).pipe(
      tap((response) => {
        const unauthorizedEntityGuid = response.headers.get('unauthorizedentityguid');
        if (unauthorizedEntityGuid) {
          sessionStorage.setItem('UnauthorizedEntityGuid', unauthorizedEntityGuid);
        }
      }),
      map((response) => response.body as ExternalPollGroupGames)
    );
  }

  /**
   * Submits an answer for an external poll
   * Mirrors submitExternalTriviaAnswer pattern with UnauthorizedEntityGuid header
   */
  submitExternalPollAnswer(pollId: number, selection: any): Observable<PollResultModel> {
    const unauthorizedEntityGuid = sessionStorage.getItem('UnauthorizedEntityGuid');
    if (unauthorizedEntityGuid) {
      const headers = this.api.getHeaders().set('UnauthorizedEntityGuid', unauthorizedEntityGuid);
      return this.api.postWithCustomHeaders(`externalgames/poll/${pollId}`, selection, headers);
    }
    return this.api.post(`externalgames/poll/${pollId}`, selection);
  }

  /**
   * Gets UnauthorizedEntityGuid from session storage and returns in a HttpHeaders object
   * @returns HttpHeader with UnauthorizedEntityGuid
   */
  getUnauthorizedEntityHeader(): HttpHeaders {
    return this.api.getHeaders().set('UnauthorizedEntityGuid', sessionStorage.getItem('UnauthorizedEntityGuid') ?? '');
  }

  /**
   * Updates the UnauthorizedEntityGuid in session storage from api response if not already set
   * @param response API response
   */
  setUnauthorizedEntityGuid(response: any) {
    if (response.headers.get('UnauthorizedEntityGuid') && !sessionStorage.getItem('UnauthorizedEntityGuid')) {
      sessionStorage.setItem('UnauthorizedEntityGuid', response.headers.get('UnauthorizedEntityGuid'));
    }
  }
}
